import { FC, useEffect, useMemo, useRef, useState } from 'react';
import FootPrintMap from './FootPrintMap';
import V2DetailGroup from '@/common/components/Feedback/V2DetailItem/Group';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import styles from '../index.module.less';
import { Col, Row } from 'antd';
import { DynamicDetail } from '@/common/components/business/StoreDetail';
import { useMethods } from '@lhb/hook';
import { isBottomOut } from '@/common/utils/ways';
import V2Title from '@/common/components/Feedback/V2Title';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { getChancePointList } from '@/common/api/expandStore/chancepoint';
import ChanceDetailDrawer from '@/common/components/business/ExpandStore/ChanceDetailDrawer';
import { getSpotRelativeTaskPage } from '@/common/api/expandStore/spotSurvey';
import AllTaskDetailDrawer, { AllTaskDetailDrawerData } from '@/views/expandstore/pages/expansiontask/components/AllTaskDetailDrawer';
import IconFont from '@/common/components/IconFont';
import copy from 'copy-to-clipboard';
import { formatSpotSurveyCoordinates } from '../coordinates';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

/**
 * @Description
 */
const Main:FC<{
  mainHeight,
  headerRef,
  detail,
  standardChancePointAlias,
  standardTaskAlias,
  /** 是否禁用（分享时禁用操作） */
  disabled,
  isSjtb,
  shareData,
  refreshChanceFlag: number,
}> = ({ mainHeight, headerRef, detail, standardChancePointAlias, standardTaskAlias, disabled, refreshChanceFlag,
  isSjtb,
  shareData,
}) => {

  const scrollContainerRef = useRef<any>();
  const coordinates = formatSpotSurveyCoordinates(detail?.lng, detail?.lat);
  const dynamicTabActiveRef = useRef<any>(''); // 同步动态表单组件tabActive
  const dynamicTabsRef = useRef<any>([]); // 同步动态表单组件的tabs
  const dynamicTabContentRefs = useRef<any>([]);
  const [dynamicTabsActive, setDynamicTabsActive] = useState<string>(''); // 动态表单详情组件tabs的active
  const [chancePointList, setChancePointList] = useState<any>([]);
  const [expandStoreList, setExpandStoreList] = useState<any>([]);
  const [chancePointDrawerData, setChancePointDrawerData] = useState<any>({
    visible: false,
    id: null
  });
  const [expandStoreDrawerData, setExpandStoreDrawerData] = useState<AllTaskDetailDrawerData>({
    visible: false,
    id: null
  });


  /** 固定头部+tab高度 */
  const fixedHeight = useMemo(() => {
    return (headerRef.current?.getBoundingClientRect()?.height || 0) + 55.2;
  }, [headerRef]);

  useEffect(() => {
    scrollContainerRef && scrollContainerRef.current?.addEventListener('scroll', methods.handleScroll);
    return () => scrollContainerRef && (scrollContainerRef.current.removeEventListener('scroll', methods.handleScroll));
  }, []);

  useEffect(() => {
    if (isNotEmptyAny(detail)) {
      methods.loadChancePointList();
      methods.loadExpandStoreList();
    }
  }, [detail]);

  useEffect(() => {
    (refreshChanceFlag > 0) && methods.loadChancePointList();
  }, [refreshChanceFlag]);

  const methods = useMethods({
    copyCoordinates() {
      if (coordinates && copy(coordinates)) {
        V2Message.success('复制成功');
      }
    },
    handleScroll: () => {
      // 所有content此时距离顶部的距离，需要减去吸顶部分的高度（标题 + tabs高度）
      const offsets = dynamicTabContentRefs.current.map((refItem) => refItem?.el?.getBoundingClientRect()?.top - fixedHeight);
      // 当前tab内容所处的索引
      const activeIndex = offsets.findIndex((offset, index) => {
        const nextOffset = offsets[index + 1] || 0;
        return offset <= 0 && nextOffset > 0;
      });
      // // 当前点击tab时，查看当前点击的tab距离顶部的距离
      const targetIndex = dynamicTabContentRefs.current.findIndex((item) => item.key === dynamicTabActiveRef.current);
      const targetOffset = targetIndex !== -1 ? offsets[targetIndex] : 0;
      if (activeIndex !== -1 && dynamicTabsRef.current?.[activeIndex]) {
        // 滚动条已经滚动到底，但是可视区域顶部还在其他tab内容区域下（最后的几个tab可能内容很少，出现在一屏）
        if (isBottomOut(scrollContainerRef.current) && targetOffset > 0) {
          setDynamicTabsActive(dynamicTabActiveRef.current);
          return;
        };
        const target = dynamicTabContentRefs.current[activeIndex];
        target && target.key && setDynamicTabsActive(target.key);
      }
    },
    /** 加载关联机会点列表 */
    loadChancePointList() {
      const params = {
        page: 1,
        size: 20,
        distance: 500,
        longitude: detail.lng,
        latitude: detail.lat
      };
      getChancePointList(params).then((res) => {
        setChancePointList(isArray(res?.objectList) ? res.objectList : []);
      });
    },
    /** 加载关联拓店任务列表 */
    loadExpandStoreList() {
      const params = {
        page: 1,
        size: 20,
        distance: 500,
        spotSurveyLng: detail.lng,
        spotSurveyLat: detail.lat
      };
      getSpotRelativeTaskPage(params).then((res) => {
        setExpandStoreList(isArray(res?.objectList) ? res.objectList : []);
      });
    },
    openChancePointDetail(item) {
      if (disabled) {
        return;
      }
      setChancePointDrawerData({
        id: item.id,
        visible: true,
      });
    },
    openExpandStoreDetail(item) {
      if (disabled) {
        return;
      }
      setExpandStoreDrawerData({
        id: item.id,
        visible: true,
      });
    }
  });
  return (
    <div ref={scrollContainerRef} className={styles.mainCon} style={{ height: mainHeight }}>
      <FootPrintMap detail={detail}/>

      <V2DetailGroup block>
        <Row gutter={[17, 0]}>
          <Col span={8}><V2DetailItem label='省市区' value={(detail?.provinceName || '') + (detail?.cityName || '') + (detail?.districtName || '')}/></Col>
          <Col span={8}><V2DetailItem label={shareData?.isSjtb || isSjtb ? '机会点位置' : '踩点位置'} value={detail?.address}/></Col>
          <Col span={8}>
            <V2DetailItem
              label='经纬度'
              value={coordinates || '-'}
              rightSlot={coordinates ? {
                icon: <IconFont iconHref='pc-common-icon-ic_copy'/>,
                onIconClick: methods.copyCoordinates,
              } : undefined}/>
          </Col>
        </Row>
      </V2DetailGroup>

      {isArray(expandStoreList) && expandStoreList.length ? <>
        <V2Title divider type='H2' text={`关联${standardTaskAlias}`}/>
        <V2DetailGroup block>
          <Row gutter={[17, 0]}>
            {expandStoreList?.map((item, index) => {
              return <Col span={8} key={index}><V2DetailItem type={disabled ? 'text' : 'link'} label={`${standardTaskAlias}名称`} onClick={() => methods.openExpandStoreDetail(item)} value={item.name}/></Col>;
            })}
          </Row>
        </V2DetailGroup>
      </> : <></>}

      {isArray(chancePointList) && chancePointList.length ? <>
        <V2Title divider type='H2' text={`关联${standardChancePointAlias}`}/>
        <V2DetailGroup block>
          <Row gutter={[17, 0]}>
            {chancePointList?.map((item, index) => {
              return <Col span={8} key={index}><V2DetailItem type={disabled ? 'text' : 'link'} onClick={() => methods.openChancePointDetail(item)} label={`${standardChancePointAlias}名称`} value={item.name}/></Col>;
            })}
          </Row>
        </V2DetailGroup>
      </> : <></>}

      <DynamicDetail
        title=''
        data={detail?.propertyGroupVOList || []}
        ignoreAttach
        container={scrollContainerRef}
        fixedHeight={fixedHeight}
        dynamicTabActiveRef={dynamicTabActiveRef}
        dynamicTabsRef={dynamicTabsRef}
        dynamicTabContentRefs={dynamicTabContentRefs}
        dynamicTabsActive={dynamicTabsActive}
        setDynamicTabsActive={setDynamicTabsActive}/>

      <ChanceDetailDrawer
        id={chancePointDrawerData.id}
        open={chancePointDrawerData.visible}
        setOpen={() => { setChancePointDrawerData({ visible: false, id: null }); }}
        refreshDetail={methods.loadChancePointList}
      />

      <AllTaskDetailDrawer
        // taskId={expandStoreDrawerData.id}
        onRefresh={methods.loadExpandStoreList}
        detailDrawerData={expandStoreDrawerData}
        setShowDetailDrawer={setExpandStoreDrawerData}/>
    </div>
  );
};

export default Main;
